import React from 'react'
import type { GetServerSideProps } from 'next'
import { createServerSideHelpers } from '@trpc/react-query/server'

import { appRouter } from '@aqua/api'
import type {
  CategoryWithChildren,
  CategoryWithParentCategory,
  StoryWithKeyIndicatorsCategoriesStoryImageAndResources,
} from '@aqua/db/types'

import StatusBar from '~/components/status-bar/StatusBar'
import Preview from '~/components/story/Preview'
import Layout from '~/pages/layout'
import { getServerSideHelper } from '~/utils/server-side-herpers'

const StoryPreview = ({
  story,
  categories,
  hasError = false, // Add hasError to props
}: {
  story?: {
    // Make story and categories optional
    storiesData: StoryWithKeyIndicatorsCategoriesStoryImageAndResources
    innermostParents: {
      storyId: number
      type: CategoryWithParentCategory
    }
  }
  categories?: CategoryWithChildren[]
  hasError?: boolean
}) => {
  // Conditional rendering based on hasError
  if (hasError) {
    return (
      <Layout>
        <main className='flex min-h-screen items-center justify-center'>
          <p>Unable to load the story preview. Please try again later.</p>
        </main>
      </Layout>
    )
  }

  return (
    <Layout>
      <main className='w-full pt-36 sm:pt-24'>
        {story && categories && (
          <>
            <StatusBar isStoryPage />
            <Preview story={story} categoryData={categories} />
          </>
        )}
      </main>
    </Layout>
  )
}

export default StoryPreview

export const getServerSideProps: GetServerSideProps = async (context) => {
  try {
    const externalId = context.query.externalId as string
    const helpers = await getServerSideHelper(context)

    const story = await helpers.stories.details.fetch(externalId)
    const categories = await helpers.stories.categories.fetch()

    return {
      props: {
        story,
        categories,
        hasError: false, // Indicate successful fetch
      },
    }
  } catch (error) {
    console.error('Error fetching story details or categories:', error)
    // Return hasError: true to indicate a fetch error
    return {
      props: {
        hasError: true,
      },
    }
  }
}
